import { Mark, mergeAttributes } from '@tiptap/core';
const MAX_FIND_CHILD_SPAN_DEPTH = 20;
/**
 * Returns all next child spans, either direct children or nested deeper
 * but won't traverse deeper into child spans found, will only go MAX_FIND_CHILD_SPAN_DEPTH levels deep (default: 20)
 */
const findChildSpans = (element, depth = 0) => {
    const childSpans = [];
    if (!element.children.length || depth > MAX_FIND_CHILD_SPAN_DEPTH) {
        return childSpans;
    }
    Array.from(element.children).forEach(child => {
        if (child.tagName === 'SPAN') {
            childSpans.push(child);
        }
        else if (child.children.length) {
            childSpans.push(...findChildSpans(child, depth + 1));
        }
    });
    return childSpans;
};
const mergeNestedSpanStyles = (element) => {
    if (!element.children.length) {
        return;
    }
    const childSpans = findChildSpans(element);
    if (!childSpans) {
        return;
    }
    childSpans.forEach(childSpan => {
        var _a, _b;
        const childStyle = childSpan.getAttribute('style') || '';
        const parentStyle = ((_b = (_a = childSpan.parentElement) === null || _a === void 0 ? void 0 : _a.closest('span')) === null || _b === void 0 ? void 0 : _b.getAttribute('style')) || '';
        if (!parentStyle)
            return;
        // Parse child property names so we only inherit what the child doesn't already declare.
        const childProps = new Set(childStyle.split(';').map(s => s.trim()).filter(Boolean)
            .map(s => s.split(':')[0].trim().toLowerCase()));
        // Properties that are visual container styles and should NOT leak from parent to child.
        const skipProps = new Set(['background-color', 'background']);
        const inherited = parentStyle
            .split(';')
            .map(s => s.trim())
            .filter(Boolean)
            .filter(decl => {
            const prop = decl.split(':')[0].trim().toLowerCase();
            return !childProps.has(prop) && !skipProps.has(prop);
        });
        if (inherited.length) {
            childSpan.setAttribute('style', `${inherited.join(';')};${childStyle}`);
        }
    });
};
/**
 * This extension allows you to create text styles. It is required by default
 * for the `text-color` and `font-family` extensions.
 * @see https://www.tiptap.dev/api/marks/text-style
 */
export const TextStyle = Mark.create({
    name: 'textStyle',
    priority: 101,
    addOptions() {
        return {
            HTMLAttributes: {},
            mergeNestedSpanStyles: true,
        };
    },
    parseHTML() {
        return [
            {
                tag: 'span',
                consuming: false,
                getAttrs: element => {
                    const hasStyles = element.hasAttribute('style');
                    if (!hasStyles) {
                        return false;
                    }
                    if (this.options.mergeNestedSpanStyles) {
                        mergeNestedSpanStyles(element);
                    }
                    return {};
                },
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
    addCommands() {
        return {
            toggleTextStyle: attributes => ({ commands }) => {
                return commands.toggleMark(this.name, attributes);
            },
            removeEmptyTextStyle: () => ({ tr }) => {
                const { selection } = tr;
                // Gather all of the nodes within the selection range.
                // We would need to go through each node individually
                // to check if it has any inline style attributes.
                // Otherwise, calling commands.unsetMark(this.name)
                // removes everything from all the nodes
                // within the selection range.
                tr.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
                    // Check if it's a paragraph element, if so, skip this node as we apply
                    // the text style to inline text nodes only (span).
                    if (node.isTextblock) {
                        return true;
                    }
                    // Check if the node has no inline style attributes.
                    // Filter out non-`textStyle` marks.
                    if (!node.marks
                        .filter(mark => mark.type === this.type)
                        .some(mark => Object.values(mark.attrs).some(value => !!value))) {
                        // Proceed with the removal of the `textStyle` mark for this node only
                        tr.removeMark(pos, pos + node.nodeSize, this.type);
                    }
                });
                return true;
            },
        };
    },
});
